import { ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight } from "lucide-react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useTableSorting } from "@/hooks/use-table-sorting";
import { usePagination } from "@/hooks/use-pagination";

type BadgeVariant = "secondary" | "pending" | "approved" | "outline";
type SortDirection = "asc" | "desc" | null;

const STATUS_BADGE_MAP: Record<string, { label: string; variant: BadgeVariant }> = {
  Aberto: { label: "Aberto", variant: "secondary" },
  Pendente: { label: "Pendente", variant: "pending" },
  Fechado: { label: "Fechado", variant: "approved" },
  "Não Integrado": { label: "Não Integrado", variant: "outline" },
};

function SortIcon({ column, activeColumn, direction }: {
  column: number;
  activeColumn: number | null;
  direction: SortDirection;
}) {
  if (activeColumn !== column) return <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />;
  if (direction === "asc") return <ArrowUp className="ml-1 h-3 w-3" />;
  return <ArrowDown className="ml-1 h-3 w-3" />;
}

function StatusBadge({ status }: { status: string }) {
  const config = STATUS_BADGE_MAP[status];
  if (!config) return <>{status}</>;
  return <Badge variant={config.variant}>{config.label}</Badge>;
}

function TableSkeleton() {
  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/50">
            {Array.from({ length: 5 }).map((_, i) => (
              <TableHead key={i}>
                <Skeleton className="h-4 w-24" />
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: 5 }).map((_, rowIndex) => (
            <TableRow key={rowIndex}>
              {Array.from({ length: 5 }).map((_, cellIndex) => (
                <TableCell key={cellIndex}>
                  <Skeleton className="h-4 w-20" />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="border rounded-lg p-8 text-center text-muted-foreground">
      Nenhum dado encontrado para o filtro selecionado.
    </div>
  );
}

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  startIndex: number;
  endIndex: number;
  totalItems: number;
  onPrev: () => void;
  onNext: () => void;
  hasPrev: boolean;
  hasNext: boolean;
}

function Pagination({ currentPage, totalPages, startIndex, endIndex, totalItems, onPrev, onNext, hasPrev, hasNext }: PaginationProps) {
  return (
    <div className="flex items-center justify-between">
      <p className="text-sm text-muted-foreground">
        Mostrando {startIndex + 1} - {Math.min(endIndex, totalItems)} de {totalItems} registros
      </p>

      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={onPrev} disabled={!hasPrev}>
          <ChevronLeft className="h-4 w-4" />
          Anterior
        </Button>

        <span className="text-sm text-muted-foreground px-2">
          Página {currentPage} de {totalPages}
        </span>

        <Button variant="outline" size="sm" onClick={onNext} disabled={!hasNext}>
          Próximo
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

interface ReportPreviewTableProps {
  headers: string[];
  data: any[];
  isLoading?: boolean;
}

export function ReportPreviewTable({ headers, data, isLoading }: ReportPreviewTableProps) {
  const { sortedData, sortColumn, sortDirection, handleSort } = useTableSorting(data);
  const pagination = usePagination(sortedData);

  const onSort = (columnIndex: number) => {
    handleSort(columnIndex);
    pagination.resetPage();
  };

  const renderCell = (cell: any, columnIndex: number) => {
    if (headers[columnIndex] === "Status") {
      return <StatusBadge status={cell} />;
    }
    return cell;
  };

  if (isLoading) return <TableSkeleton />;
  if (!data?.length) return <EmptyState />;

  return (
    <div className="space-y-4">
      <div className="border rounded-lg overflow-hidden">
        <div className="max-h-[500px] overflow-y-auto">
          <Table>
            <TableHeader className="sticky top-0 bg-white z-10">
              <TableRow className="bg-muted/50">
                {headers.map((header, index) => (
                  <TableHead
                    key={index}
                    className="cursor-pointer select-none hover:bg-muted/80 transition-colors"
                    onClick={() => onSort(index)}
                  >
                    <div className="flex items-center">
                      {header}
                      <SortIcon column={index} activeColumn={sortColumn} direction={sortDirection} />
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>

            <TableBody>
              {pagination.paginatedData.map((row, rowIndex) => (
                <TableRow key={rowIndex} className="hover:bg-muted/30">
                  {row.map((cell: any, cellIndex: number) => (
                    <TableCell key={cellIndex}>
                      {renderCell(cell, cellIndex)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {pagination.totalPages > 1 && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          startIndex={pagination.startIndex}
          endIndex={pagination.endIndex}
          totalItems={sortedData.length}
          onPrev={pagination.prevPage}
          onNext={pagination.nextPage}
          hasPrev={pagination.hasPrevPage}
          hasNext={pagination.hasNextPage}
        />
      )}
    </div>
  );
}
